import {getTestProps} from '@benchling/js-test-utils';
import {Group} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo, useState} from 'react';
import ReactDOMServer from 'react-dom/server';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';

import {trackEvent} from 'analytics/Analytics';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {
  containsBnchCanvas,
  highlightCanvasPlaceholderNoteParts,
  replaceBnchCanvasTagsWithPlaceholder,
} from 'canvas-core/chat/bnchCanvasMarkdown';
import {DEEP_RESEARCH_ANALYTICS_ACTIONS} from 'constants/analytics-products/DeepResearchAnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import EntryDAO from 'dao/EntryDAO';
import {createChartNotePart} from 'llm/charts/SaveChartToEntryButton';
import {ProgressData} from 'llm/chat-library/progress/ProgressTypes';
import {useActivityModalControls} from 'llm/chat-library/tool-details/ActivityModal';
import {convertMarkdownToWord} from 'llm/deep-research/messages/MarkdownToWordConverter';
import {rehypeBenchlingTagsForSave} from 'llm/markdown/LLMMarkdownRehype';
import {remarkBenchlingTags} from 'llm/markdown/LLMMarkdownRemark';
import LLMMarkdownSection, {
  createBenchlingTagConfigs,
  createSanitizeSchema,
  LLMMarkdownClassNames,
  SaveToEntryOption,
} from 'llm/markdown/LLMMarkdownSection';
import {BenchlingTagConfigs, cleanMarkdownForSaving} from 'llm/markdown/LLMMarkdownUtils';
import CitationSourceChip, {WebCitationSource} from 'llm/markdown/SourceChip';
import {getActiveWorkspaceEntry, openAddToEntryModal} from 'llm/modals/AddToEntryModal';
import wyciwyp from 'notebook/mediocre/clipboard/wyciwyp';
import {WYCIWYPPart} from 'notebook/mediocre/clipboard/WyciwypConstants';
import * as TableMapUtil from 'notebook/mediocre/util/TableMapUtil';
import {makeDefaultItem, makeItemFromWyciwypPart} from 'notebook/notes/CreateOps';
import EntryUtil from 'notebook/util/EntryUtil';
import {NotePart, NotePartType} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import AbsoluteDropdownContainer from 'ui-library/AbsoluteDropdownContainer';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {OverflowButton} from 'ui-library/menus/OverflowMenu';
import Section from 'ui-library/menus/Section';
import {remarkGfmTables} from 'ui-library/remarkGfmTables';
import {readableTime} from 'util/DateUtil';
import {sanitizeName} from 'util/FileUtil';
import * as logging from 'util/Logging';
import {initiateFileDownload} from 'util/TextToBlobUtil';

import {AssistantMessage, DisplayMessageAssistantResponseType} from './MessageTypes';
import {getDisplayMessageAssistantResponseType, getMessageResponseTypeAsText} from './MessageUtils';
import {
  SaveChatAssistantMessageAsEntryOrTemplateModal,
  SaveChatAssistantMessageAsEntryOrTemplateResult,
} from './SaveChatAssistantMessageAsEntryOrTemplateModal';

export async function getUsedTableNames(entryId: string): Promise<Array<string>> {
  const entryData = await EntryDAO.get(entryId);
  const entry = EntryUtil.prepareForStore(entryData!, {});
  return Object.keys(TableMapUtil.getNoteTableMap(entryId, entry.note));
}

const CHART_ID_PATTERN = 'chart_[a-zA-Z0-9]{8,16}';
const CHART_TAG_REGEX = new RegExp(`<bnch_chart>(${CHART_ID_PATTERN})<\\/bnch_chart>`, 'g');
const CHART_MARKER_PREFIX = 'BNCH_CHART_MARKER_';

const CHAT_ASSISTANT_UNTITLED_CHAT_LABEL = 'Untitled Chat';
const CANVAS_CANNOT_BE_SAVED_TO_ENTRY_MESSAGE = 'Canvases cannot be inserted into entries yet.';
function assistantMessageTimestampForSaveLabels(assistantMessage: AssistantMessage): string {
  return readableTime(assistantMessage.createdAt, {showTime: true});
}

function createSanitizeSchemaForSave(
  tagConfigs: BenchlingTagConfigs
): ReturnType<typeof createSanitizeSchema> {
  const schema = createSanitizeSchema(tagConfigs);
  const attributes = {...schema.attributes};
  delete attributes.a;
  return {
    ...schema,
    // The save path enables link formats for generated citation anchors only.
    tagNames: schema.tagNames.filter((tagName) => tagName !== 'a'),
    attributes,
  };
}

/**
 * Default name for Save-as-Entry/Template and Word download flows.
 * Report markdown with a leading `#` title uses that title after **`sanitizeName`**;
 * otherwise uses `{session} - {Message|Report} ({locale time})` with OS-safe sanitization.
 */
export function buildNameForSaveAs(param: {
  markdownContent: string;
  chatSessionDisplayName: string | null | undefined;
  assistantMessage: AssistantMessage;
}): string {
  const responseType = getDisplayMessageAssistantResponseType(param.assistantMessage);
  if (responseType === DisplayMessageAssistantResponseType.REPORT) {
    const firstHeaderMatch = param.markdownContent.match(/^#\s+(.+)$/m);
    const headerTitle = firstHeaderMatch?.[1]?.trim();
    if (headerTitle) {
      return sanitizeName(headerTitle);
    }
  }
  const session =
    param.chatSessionDisplayName != null && param.chatSessionDisplayName.trim().length > 0
      ? param.chatSessionDisplayName.trim()
      : CHAT_ASSISTANT_UNTITLED_CHAT_LABEL;
  const timestampLabel = assistantMessageTimestampForSaveLabels(param.assistantMessage);
  const contentKindLabel = getMessageResponseTypeAsText(responseType);
  return sanitizeName(`${session} - ${contentKindLabel} (${timestampLabel})`);
}

function extractAndReplaceChartTags(markdown: string): string {
  return markdown.replace(
    CHART_TAG_REGEX,
    (_match, chartId: string) => `\n\n${CHART_MARKER_PREFIX}${chartId}\n\n`
  );
}

function replaceChartMarkersInNoteParts(noteParts: Array<NotePart>): Array<NotePart> {
  const markerRegex = new RegExp(`^\\s*${CHART_MARKER_PREFIX}(${CHART_ID_PATTERN})\\s*$`);
  return noteParts.flatMap((notePart) => {
    if (notePart.type !== NotePartType.TEXT || !notePart.text) {
      return [notePart];
    }
    const match = notePart.text.match(markerRegex);
    if (match) {
      return [createChartNotePart(match[1])];
    }
    return [notePart];
  });
}

/**
 * Converts LLM markdown (with custom Benchling tags) into note parts for a notebook entry.
 * - Extracts chart tags and replaces them with markers before processing.
 * - Replaces canvas tags with a text placeholder; canvases cannot be saved to entries yet.
 * - Removes UI-only tags, preserving only tags needed for entry-saving.
 * - Renders markdown to sanitized HTML, then parses to note parts.
 * - Replaces chart marker note parts with proper note_linked_chart note parts.
 * - Returns client-shaped NotePart data; the DAO layer will transform to server format.
 */
export function convertMarkdownToNoteParts(
  markdownContent: string,
  options: {usedTableNames: Array<string>}
): Array<NotePart> {
  const processedMarkdown = replaceBnchCanvasTagsWithPlaceholder(extractAndReplaceChartTags(markdownContent));

  const tagConfigs = createBenchlingTagConfigs({});
  const cleanedMarkdown = cleanMarkdownForSaving(processedMarkdown, tagConfigs);

  // Special rendering for the components of custom tags that need to be saved
  const saveComponents = Object.fromEntries(
    tagConfigs.tagConfigs
      .filter((config) => config.preserveContentWhenSaving === SaveToEntryOption.PRESERVE_TAGS)
      .map((config) => [config.htmlTagName, config.savingComponent])
  );

  // Create config for sanitization keeping only tags that need to be preserved
  const sanitizationTagConfigs: BenchlingTagConfigs = {
    tagConfigs: tagConfigs.tagConfigs.filter(
      (config) => config.preserveContentWhenSaving === SaveToEntryOption.PRESERVE_TAGS
    ),
  };
  const sanitizationSchema = createSanitizeSchemaForSave(sanitizationTagConfigs);

  const htmlContent = ReactDOMServer.renderToStaticMarkup(
    <ReactMarkdown
      remarkPlugins={[[remarkBenchlingTags, tagConfigs], remarkGfmTables]}
      rehypePlugins={[
        [rehypeBenchlingTagsForSave, tagConfigs],
        [rehypeSanitize, sanitizationSchema],
      ]}
      components={saveComponents}
    >
      {cleanedMarkdown}
    </ReactMarkdown>
  );

  const mockClipboardData = {
    types: ['text/html', 'text/plain'],
    getData: (type: string) => {
      if (type === 'text/html') {
        return htmlContent;
      }
      if (type === 'text/plain') {
        return cleanedMarkdown;
      }
      return '';
    },
  } as unknown as DataTransfer;

  const wyciwypParts = wyciwyp.processClipboard(mockClipboardData, {
    allowTables: true,
    allowHeaders: true,
    allowLinks: true,
  });

  const {usedTableNames} = options;

  const noteparts = wyciwypParts.map((part: WYCIWYPPart) => {
    const {item} = makeItemFromWyciwypPart(part, usedTableNames, true);
    const notePartBase = makeDefaultItem(item.type, null, {initialNotePart: false});
    let notePart;
    switch (item.type) {
      case NotePartType.TABLE:
        assert(item.table != null, 'Expected table to be present when item type is TABLE', {
          itemType: item.type,
        });
        notePart = {
          ...notePartBase,
          table: item.table,
          indentation: item.indentation,
          headerType: null,
        };
        break;
      case NotePartType.TEXT:
      case NotePartType.CODE:
        notePart = {
          ...notePartBase,
          text: item.text,
          indentation: item.indentation,
          listType: item.listType,
          formats: item.formats!,
          headerType: item.headerType,
        };
        break;
      default:
        throw new Error(`Unsupported note part type from wyciwyp: ${item.type}`);
    }
    return notePart;
  });

  return highlightCanvasPlaceholderNoteParts(replaceChartMarkersInNoteParts(noteparts));
}

interface CitationMaps {
  toolCitationMap: Map<string, number>;
  webCitations: Set<string>;
}

const createCitationMaps = (markdown: string): CitationMaps => {
  // Extract tool citations
  const toolCitationRegex = /<bnch_citation>(.*?)<\/bnch_citation>/g;
  const toolCitationIds = Array.from(
    new Set(Array.from(markdown.matchAll(toolCitationRegex), (match) => match[1]))
  );

  const toolCitationMap = new Map<string, number>();
  toolCitationIds.forEach((id, index) => {
    toolCitationMap.set(id, index + 1);
  });

  // Extract web citations
  const webCitationRegex = /<web_citation>(.*?)<\/web_citation>/g;
  const webCitationUrls = Array.from(
    new Set(Array.from(markdown.matchAll(webCitationRegex), (match) => match[1]))
  );
  const webCitations = new Set(webCitationUrls);

  return {toolCitationMap, webCitations};
};

export interface ChatAssistantReportMessageContentsProps {
  report: string;
  onCitationClick?: (toolId: string) => void;
  progressData?: ProgressData | null;
  citationData?: ProgressData | null;
  readonly?: boolean;
  chatSessionId: string;
  markdownClassNames?: LLMMarkdownClassNames;
}

export function ChatAssistantReportMessageContents({
  report,
  progressData,
  citationData,
  readonly,
  chatSessionId,
  markdownClassNames,
}: ChatAssistantReportMessageContentsProps): JSX.Element {
  const {openActivityModal} = useActivityModalControls();
  const {toolCitationMap, webCitations} = useMemo(() => createCitationMaps(report), [report]);
  const toolCallData = citationData ?? progressData;

  const reportToolCalls: ProgressData | null = useMemo(() => {
    if (!toolCallData) {
      return null;
    }
    const citedIds = new Set(toolCitationMap.keys());
    const cited = toolCallData.toolCalls.filter((tc) => citedIds.has(tc.toolId));
    return cited.length > 0 ? {toolCalls: cited} : null;
  }, [toolCallData, toolCitationMap]);

  const onCitationClick = useCallback(
    (citation: string, isSourceCitation = false) => {
      const toolType = toolCallData?.toolCalls.find((tool) => tool.toolId === citation)?.tool;

      trackEvent(DEEP_RESEARCH_ANALYTICS_ACTIONS.CLICKED_DEEP_RESEARCH_CITATION, {
        chat_session_id: chatSessionId,
        is_source_citation: isSourceCitation,
        tool_type: toolType,
      });

      if (!reportToolCalls) {
        if (readonly) {
          return;
        }
        logging.error('[llm] Citation clicked but no progress data available', {
          extra: {citation},
        });
        return;
      }
      openActivityModal(reportToolCalls, chatSessionId, citation);
    },
    [toolCallData, reportToolCalls, openActivityModal, readonly, chatSessionId]
  );
  const hasWebCitations = webCitations.size > 0;
  const hasToolCitations = toolCitationMap.size > 0;
  return (
    <>
      <LLMMarkdownSection
        markdown={report}
        onCitationClick={onCitationClick}
        progressData={reportToolCalls || undefined}
        citationNumberMap={toolCitationMap}
        chatSessionId={chatSessionId}
        classNames={markdownClassNames}
        canSaveChartsToEntry={!readonly}
      />
      {(hasToolCitations || hasWebCitations) && (
        <div
          className={css(styles.sourcesSection)}
          {...getTestProps({component: 'ChatAssistantReportMessageContents', element: 'sources'})}
        >
          <Group spacing={4} align="center">
            <h4>Sources</h4>
          </Group>
          {hasToolCitations && (
            <>
              {hasWebCitations && <h5>Benchling Citations</h5>}
              <ol className={css(styles.sourcesContainer)}>
                {Array.from(toolCitationMap.entries()).map(([citationId, citationNumber]) => (
                  <CitationSourceChip
                    key={citationId}
                    citationId={citationId}
                    citationNumber={citationNumber}
                    onCitationClick={onCitationClick}
                    toolCall={reportToolCalls?.toolCalls.find((tool) => tool.toolId === citationId)}
                  />
                ))}
              </ol>
            </>
          )}
          {hasWebCitations && (
            <>
              {hasToolCitations && <h5>Web Citations</h5>}
              <ol className={css(styles.sourcesContainer)}>
                {Array.from(webCitations).map((url) => (
                  <WebCitationSource key={url} url={url} />
                ))}
              </ol>
            </>
          )}
        </div>
      )}
    </>
  );
}

export function SaveChatAssistantReportButton({
  markdownReport,
  chatSessionId,
  chatSessionDisplayName,
  assistantMessage,
}: {
  markdownReport: string;
  chatSessionId: string;
  chatSessionDisplayName?: string | null;
  assistantMessage: AssistantMessage;
}): JSX.Element {
  const saveDropdownMenu = (
    <ChatAssistantSaveDropdownMenu
      markdownReport={markdownReport}
      chatSessionId={chatSessionId}
      chatSessionDisplayName={chatSessionDisplayName}
      assistantMessage={assistantMessage}
      className="dropdown-menu pull-right"
    />
  );

  return (
    <Dropdown menu={saveDropdownMenu} className="u-display-inline-block dropdown">
      <Button buttonStyle={ButtonStyle.PRIMARY} size={ButtonSize.SMALL} text="Save as..." isDropdown={true} />
    </Dropdown>
  );
}

interface ChatAssistantSaveDropdownContext {
  markdownReport: string;
  chatSessionId: string;
  chatSessionDisplayName?: string | null;
  assistantMessage: AssistantMessage;
}

function renderChatAssistantSaveDropdownOptions(
  context: ChatAssistantSaveDropdownContext
): Array<JSX.Element> {
  const {markdownReport, chatSessionId, chatSessionDisplayName, assistantMessage} = context;
  const responseType = getDisplayMessageAssistantResponseType(assistantMessage);
  const contentKindLabel = getMessageResponseTypeAsText(responseType);
  const contentKindLower = contentKindLabel.toLowerCase();

  const notifyIfCanvasCannotBeSavedToEntry = (): void => {
    if (containsBnchCanvas(markdownReport)) {
      notifications.warn(CANVAS_CANNOT_BE_SAVED_TO_ENTRY_MESSAGE);
    }
  };

  const handleSaveClick = (targetType: string): void => {
    const noteParts = convertMarkdownToNoteParts(markdownReport, {usedTableNames: []});
    trackEvent(DEEP_RESEARCH_ANALYTICS_ACTIONS.SAVED_DEEP_RESEARCH_TO_ENTRY, {
      targetType,
    });

    legacyRunModal(SaveChatAssistantMessageAsEntryOrTemplateModal, {
      targetType,
      chatSessionId,
      initialNoteParts: noteParts,
      initialName: buildNameForSaveAs({
        markdownContent: markdownReport,
        chatSessionDisplayName,
        assistantMessage,
      }),
      messageResponseType: responseType,
      onRequestSubmit: (result: SaveChatAssistantMessageAsEntryOrTemplateResult) => {
        const targetName = result.targetType === ModelTypeConstants.ENTRY ? 'Entry' : 'Template';
        notifications.success(
          `${targetName} created and ${contentKindLower} content has been automatically added!`
        );
        notifyIfCanvasCannotBeSavedToEntry();
      },
    });
  };

  const handleAddToEntryClick = async (): Promise<void> => {
    trackEvent(DEEP_RESEARCH_ANALYTICS_ACTIONS.SAVED_DEEP_RESEARCH_TO_ENTRY);
    const result = await openAddToEntryModal({
      notePartsContentSourceId: chatSessionId,
      getContentsToInsert: async (entryId) => {
        const noteParts = convertMarkdownToNoteParts(markdownReport, {
          usedTableNames: await getUsedTableNames(entryId),
        });
        return {noteParts};
      },
      initialSelectedEntry: getActiveWorkspaceEntry(),
    });
    if (result) {
      notifications.success(
        `${contentKindLabel} content has been added to the selected entry! Navigating to entry...`
      );
      notifyIfCanvasCannotBeSavedToEntry();
    }
  };

  const handleDownloadAsWord = async (): Promise<void> => {
    trackEvent(DEEP_RESEARCH_ANALYTICS_ACTIONS.SAVED_DEEP_RESEARCH_TO_ENTRY, {
      targetType: 'word_document',
    });

    const blob = await convertMarkdownToWord(markdownReport, {chatSessionId});

    const filenameRoot = sanitizeName(
      buildNameForSaveAs({
        markdownContent: markdownReport,
        chatSessionDisplayName,
        assistantMessage,
      })
    );

    const filename = `${filenameRoot}.docx`;

    initiateFileDownload(filename, URL.createObjectURL(blob));

    notifications.success(`${contentKindLabel} has been downloaded as a Word document!`);
  };

  const options = [
    <Option
      key={ModelTypeConstants.TEMPLATE}
      value={ModelTypeConstants.TEMPLATE}
      onSelect={() => {
        handleSaveClick(ModelTypeConstants.TEMPLATE);
      }}
      icon="template"
    >
      Save as Template
    </Option>,
    <Option key="add-to-entry" value="add-to-entry" onSelect={handleAddToEntryClick} icon="plus">
      Add to Existing Entry
    </Option>,
    <Option key="download-word" value="download-word" onSelect={handleDownloadAsWord} icon="download">
      Save as Word
    </Option>,
  ];

  if (BenchlingConfig.ELN_ENABLE_BLANK_ENTRY_CREATION) {
    options.unshift(
      <Option
        key={ModelTypeConstants.ENTRY}
        value={ModelTypeConstants.ENTRY}
        onSelect={() => {
          handleSaveClick(ModelTypeConstants.ENTRY);
        }}
        icon="entry"
      >
        Save as Entry
      </Option>
    );
  }

  return options;
}

interface ChatAssistantSaveDropdownMenuProps extends ChatAssistantSaveDropdownContext {
  className?: string;
  dropLeft?: boolean;
}

function ChatAssistantSaveDropdownMenu({
  markdownReport,
  chatSessionId,
  chatSessionDisplayName,
  assistantMessage,
  className,
  dropLeft = false,
}: ChatAssistantSaveDropdownMenuProps): JSX.Element {
  return (
    <Menu height={Menu.HEIGHTS.SMALL} width={Menu.WIDTHS.MEDIUM} className={className} dropLeft={dropLeft}>
      {renderChatAssistantSaveDropdownOptions({
        markdownReport,
        chatSessionId,
        chatSessionDisplayName,
        assistantMessage,
      })}
    </Menu>
  );
}

export function SaveChatAssistantReplyOverflowButton({
  markdownReport,
  chatSessionId,
  chatSessionDisplayName,
  assistantMessage,
  onOpenChange,
}: {
  markdownReport: string;
  chatSessionId: string;
  chatSessionDisplayName?: string | null;
  assistantMessage: AssistantMessage;
  onOpenChange?: (isOpen: boolean) => void;
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <AbsoluteDropdownContainer
      menu={
        <Menu height={Menu.HEIGHTS.SMALL} width={Menu.WIDTHS.MEDIUM}>
          <Section>
            {renderChatAssistantSaveDropdownOptions({
              markdownReport,
              chatSessionId,
              chatSessionDisplayName,
              assistantMessage,
            })}
          </Section>
        </Menu>
      }
      onRequestClose={() => {
        setIsOpen(false);
        onOpenChange?.(false);
      }}
      open={isOpen}
      shouldCloseOnInsideClick={true}
    >
      <OverflowButton
        enableTooltip={true}
        onClick={() => {
          setIsOpen(true);
          onOpenChange?.(true);
        }}
        dataTestAttrs={{component: 'ChatAssistantMessageBubble', element: 'save-overflow-button'}}
      />
    </AbsoluteDropdownContainer>
  );
}

const styles = StyleSheet.create({
  sourcesSection: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    paddingBottom: '16px',
  },
  sourcesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    padding: '0px',
  },
});
